"use client";

import React, { useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ja, enUS } from "date-fns/locale";

export type PeriodRangePickerProps = {
  startFrom: string | null;
  startTo: string | null;
  onChangeStartFrom: (v: string | null) => void;
  onChangeStartTo: (v: string | null) => void;

  locale?: "ja" | "en";
  endMustBeAfterStart?: boolean;

  /** 表示 */
  label?: string;
  startPlaceholder?: string; 
  endPlaceholder?: string;   
  emptyHint?: string;        
  invalidMessage?: string;    

  /** ポータル/テスト */
  portalId?: string;         
  testIdStart?: string;      
  testIdEnd?: string;        
};

/* ================= util ================= */

const toYmd = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const parseYmdLocal = (ymd: string) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
};

const normalize = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

const addDays = (d: Date, days: number) => {
  const x = normalize(d);
  x.setDate(x.getDate() + days);
  return x;
};

/* ======================================== */

export const PeriodRangePicker: React.FC<PeriodRangePickerProps> = ({
  startFrom,
  startTo,
  onChangeStartFrom,
  onChangeStartTo,
  locale = "ja",
  endMustBeAfterStart = false,

  label,
  startPlaceholder = "Start date",
  endPlaceholder = "End date",
  emptyHint,
  invalidMessage,

  portalId,
  testIdStart,
  testIdEnd,
}) => {
  const selectedStart = useMemo(
    () => (startFrom ? parseYmdLocal(startFrom) : null),
    [startFrom]
  );
  
  const selectedEnd = useMemo(
    () => (startTo ? parseYmdLocal(startTo) : null),
    [startTo]
  );

  const minEndDate = useMemo(() => {
    if (!selectedStart) return undefined;
    return endMustBeAfterStart ? addDays(selectedStart, 1) : normalize(selectedStart);
  }, [selectedStart, endMustBeAfterStart]);

  const currentLocale = locale === "ja" ? ja : enUS;

  const isInvalidRange = useMemo(() => {
    if (!startFrom || !startTo) return false;
    const s = parseYmdLocal(startFrom);
    const e = parseYmdLocal(startTo);
    const minE = endMustBeAfterStart ? addDays(s, 1) : normalize(s);
    return e < minE;
  }, [startFrom, startTo, endMustBeAfterStart]);

  const showEmptyHint = !!emptyHint && !startFrom && !startTo;

  return (
    <div className="space-y-2">
      {label && <div className="text-xs px-2 text-white/80">{label}</div>}

      <div className="flex flex-col md:flex-row gap-2">
        {/* START */}
        <div className="flex-1 min-w-0">
          <DatePicker
            data-testid={testIdStart || "period-start"}
            selected={selectedStart}
            onChange={(date) => {
              const v = date ? toYmd(date) : null;
              onChangeStartFrom(v);
              if (v && startTo) {
                const s = parseYmdLocal(v);
                const e = parseYmdLocal(startTo);
                const minE = endMustBeAfterStart ? addDays(s, 1) : normalize(s);
                if (e < minE) onChangeStartTo(toYmd(minE));
              }
            }}
            isClearable
            placeholderText={startPlaceholder}
            locale={currentLocale}
            dateFormat="yyyy-MM-dd"
            popperPlacement="bottom-start"
            popperContainer={({ children }) => <div className="relative z-[9999]">{children}</div>}
            wrapperClassName="w-full"
            className="text-black w-full px-3 py-2 rounded border border-white/20 text-sm"
            portalId={portalId}
          />
        </div>

        <div className="flex-1 min-w-0">
          <DatePicker
            data-testid={testIdEnd || "period-end"}
            selected={selectedEnd}
            onChange={(date) => onChangeStartTo(date ? toYmd(date) : null)}
            minDate={minEndDate}
            isClearable
            placeholderText={endPlaceholder}
            locale={currentLocale}
            dateFormat="yyyy-MM-dd"
            popperPlacement="bottom-start"
            popperContainer={({ children }) => <div className="relative z-[9999]">{children}</div>}
            wrapperClassName="w-full"
            className="text-black w-full px-3 py-2 rounded border border-white/20 text-sm"
            portalId={portalId}
          />
        </div>
      </div>

      {/* {showEmptyHint && <div className="text-xs text-white/60">{emptyHint}</div>} */}
      {isInvalidRange && invalidMessage && (
        <div className="text-xs text-red-400">{invalidMessage}</div>
      )}
    </div>
  );
};